import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import metaLogo from '@/assets/meta-logo.svg';
import { Switch } from '@/components/ui/Switch';
import { useUpdateAudience } from '@/hooks/useUpdateAudience';
import { Audience, EAudiencePlatform } from '@/services/AudiencesService';

interface MetaSwitchProps {
  audience: Pick<Audience, 'id' | 'audiencePlatform'>;
}

export function MetaSwitch({ audience }: MetaSwitchProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Behaviors.AudiencesCreate.components.MetaSwitch',
  });

  const { updateAudience, isLoading } = useUpdateAudience(audience.id);

  const metaAudience = audience.audiencePlatform.find(
    (audiencePlatform) => audiencePlatform.platform === EAudiencePlatform.META,
  );

  const [checked, setChecked] = useState(metaAudience?.isSynchronized);

  async function handleCheckedChange() {
    if (metaAudience?.isSynchronized) {
      toast.error(t('toast.alreadySynchronized'));
      return;
    }

    try {
      await updateAudience({ platform: EAudiencePlatform.META });
      toast.success(t('toast.success'));
      setChecked(true);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        toast.error(t('toast.error'));
        setChecked(false);
      }
    }
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex cursor-default items-center gap-2">
        <img
          src={metaLogo}
          alt="Meta Logo"
          className="h-20 w-20 object-contain"
        />
      </div>

      <Switch
        checked={checked}
        disabled={isLoading}
        onCheckedChange={handleCheckedChange}
      />
    </div>
  );
}
